import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Données des slides pour le panneau gauche avec images de camions
// Image du camion répétée pour tous les slides jusqu'à ce que les vraies images soient fournies
const truckImage = 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=2940&auto=format&fit=crop';

const slides = [
  {
    id: 1,
    title: 'Connectez-vous à LogiConnect',
    description: 'La plateforme de transport qui simplifie vos expéditions et connecte les entrepreneurs aux transporteurs.',
    image: truckImage,
  },
  {
    id: 2,
    title: 'Transportez en toute confiance',
    description: 'Suivez vos marchandises en temps réel et gérez vos expéditions depuis une interface intuitive.',
    image: truckImage,
  },
  {
    id: 3,
    title: 'Développez votre activité',
    description: 'Rejoignez une communauté de professionnels et développez votre réseau de transport.',
    image: truckImage,
  },
];

const AuthLayout = ({ children, currentSlide = 0 }) => {
  const [activeSlide, setActiveSlide] = useState(currentSlide);

  // Auto-rotation des slides toutes les 5 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative">
      {/* Panneau gauche - Images défilantes (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Images en background avec animation */}
        <AnimatePresence mode="wait">
          {slides.map((slide, index) => {
            if (index !== activeSlide) return null;
            return (
              <motion.div
                key={slide.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${slide.image})`,
                }}
              />
            );
          })}
        </AnimatePresence>

        {/* Overlay sombre avec couleur primaire */}
        <div className="absolute inset-0 bg-[#0d5850]/70 z-10" />

        {/* Logo en haut à gauche - Navigation */}
        <Link to="/" className="absolute top-8 left-8 z-20 flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center p-2">
            <img
              src="/logo.png"
              alt="LogiConnect"
              className="w-full h-full object-contain"
            />
          </div>
          <span className="text-2xl font-bold text-white">LogiConnect</span>
        </Link>

        {/* Textes superposés sur les images */}
        <AnimatePresence mode="wait">
          {slides.map((slide, index) => {
            if (index !== activeSlide) return null;
            return (
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center z-10"
              >
                {/* Container avec background transparent en couleur secondaire */}
                <div className="bg-amber-500/20 backdrop-blur-sm rounded-2xl p-8 max-w-3xl">
                  {/* Texte d'appel */}
                  <motion.h2
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="text-4xl font-bold text-white mb-6 leading-tight"
                  >
                    {slide.title}
                  </motion.h2>

                  <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="text-lg text-white/95 leading-relaxed"
                  >
                    {slide.description}
                  </motion.p>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Contrôles de navigation */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4">
          <button
            onClick={prevSlide}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors flex items-center justify-center text-white"
            aria-label="Slide précédent"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Indicateurs de slides */}
          <div className="flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === activeSlide
                    ? 'w-8 bg-white'
                    : 'w-2 bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Aller au slide ${index + 1}`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors flex items-center justify-center text-white"
            aria-label="Slide suivant"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Header mobile - Version compacte du panneau gauche */}
      <div className="lg:hidden relative overflow-hidden">
        {/* Images en background avec animation */}
        <AnimatePresence mode="wait">
          {slides.map((slide, index) => {
            if (index !== activeSlide) return null;
            return (
              <motion.div
                key={slide.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${slide.image})`,
                }}
              />
            );
          })}
        </AnimatePresence>

        {/* Overlay sombre avec couleur primaire */}
        <div className="absolute inset-0 bg-[#0d5850]/70 z-10" />

        {/* Logo et titre */}
        <div className="flex items-center justify-between p-4 z-20 relative">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center p-1.5">
              <img
                src="/logo.png"
                alt="LogiConnect"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-xl font-bold text-white">LogiConnect</span>
          </Link>
        </div>

        {/* Carrousel mobile compact */}
        <div className="relative h-48 pb-4">
          <AnimatePresence mode="wait">
            {slides.map((slide, index) => {
              if (index !== activeSlide) return null;
              return (
                <motion.div
                  key={slide.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center z-10"
                >
                  {/* Container avec background transparent en couleur secondaire */}
                  <div className="bg-amber-500/20 backdrop-blur-sm rounded-xl p-4 w-full max-w-sm">
                    {/* Texte d'appel réduit */}
                    <motion.h2
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1, duration: 0.3 }}
                      className="text-lg font-bold text-white mb-2 leading-tight"
                    >
                      {slide.title}
                    </motion.h2>

                    <motion.p
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.3 }}
                      className="text-xs text-white/95 leading-relaxed line-clamp-2"
                    >
                      {slide.description}
                    </motion.p>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* Contrôles de navigation mobile */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            <button
              onClick={prevSlide}
              className="w-8 h-8 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors flex items-center justify-center text-white"
              aria-label="Slide précédent"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Indicateurs de slides */}
            <div className="flex gap-1.5">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  className={`h-1.5 rounded-full transition-all ${
                    index === activeSlide
                      ? 'w-6 bg-white'
                      : 'w-1.5 bg-white/40'
                  }`}
                  aria-label={`Aller au slide ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="w-8 h-8 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors flex items-center justify-center text-white"
              aria-label="Slide suivant"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Panneau droit - Formulaires */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 p-6 lg:p-12 relative overflow-hidden">
        <div className="w-full max-w-full">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
